import { IMobileSdkFilterOptions } from 'src/apps/mobile-sdk/hooks/useMobileSdkFilterOptionsQuery';
import {
	IGetMobileSdkTestIssuesParams,
	IGetMobileSdkTestsParams
} from 'src/apps/mobile-sdk/services/MobileSdkDashboardServiceTypes';
import { transformMobileSdkFilterOptionsResults } from 'src/apps/mobile-sdk/services/queries/filter-options/helpers/transformResults';
import { MobileSdkFilterOptionsQuery } from 'src/apps/mobile-sdk/services/queries/filter-options/MobileSdkFilterOptions.generated';
import { MobileSdkFilterOptions } from 'src/apps/mobile-sdk/services/queries/filter-options/MobileSdkFilterOptions.gql';
import { buildMobileSdkIssueDetailsVariables } from 'src/apps/mobile-sdk/services/queries/issue-details/helpers/buildVariables';
import { transformMobileSdkIssueDetailsResults } from 'src/apps/mobile-sdk/services/queries/issue-details/helpers/transformResults';
import { MobileSdkIssueDetailsQuery } from 'src/apps/mobile-sdk/services/queries/issue-details/MobileSdkIssueDetails.generated';
import { MobileSdkIssueDetails } from 'src/apps/mobile-sdk/services/queries/issue-details/MobileSdkIssueDetails.gql';
import { buildMobileSdkTestDetailsQueryVariables } from 'src/apps/mobile-sdk/services/queries/test-details/helpers/buildVariables';
import { transformMobileSdkTestDetailsResults } from 'src/apps/mobile-sdk/services/queries/test-details/helpers/transformResults';
import { MobileSdkTestDetailsQuery } from 'src/apps/mobile-sdk/services/queries/test-details/MobileSdkTestDetails.generated';
import { MobileSdkTestDetails } from 'src/apps/mobile-sdk/services/queries/test-details/MobileSdkTestDetails.gql';
import { buildMobileSdkTestFilterOptionsVariables } from 'src/apps/mobile-sdk/services/queries/test-filter-options/helpers/buildVariables';
import { transformMobileSdkTestFilterOptionsResults } from 'src/apps/mobile-sdk/services/queries/test-filter-options/helpers/transformResults';
import { MobileSdkTestFilterOptionsQuery } from 'src/apps/mobile-sdk/services/queries/test-filter-options/MobileSdkTestFilterOptions.generated';
import { MobileSdkTestFilterOptions } from 'src/apps/mobile-sdk/services/queries/test-filter-options/MobileSdkTestFilterOptions.gql';
import { buildMobileSdkTestIssuesQueryVariables } from 'src/apps/mobile-sdk/services/queries/test-issues/helpers/buildVariables';
import { transformMobileSdkTestIssuesResults } from 'src/apps/mobile-sdk/services/queries/test-issues/helpers/transformResults';
import { MobileSdkTestIssuesQuery } from 'src/apps/mobile-sdk/services/queries/test-issues/MobileSdkTestIssues.generated';
import { MobileSdkTestIssues } from 'src/apps/mobile-sdk/services/queries/test-issues/MobileSdkTestIssues.gql';
import { buildMobileSdkTestsQueryVariables } from 'src/apps/mobile-sdk/services/queries/tests/helpers/buildVariables';
import { transformMobileSdkTestsResults } from 'src/apps/mobile-sdk/services/queries/tests/helpers/transformResults';
import { MobileSdkTestsQuery } from 'src/apps/mobile-sdk/services/queries/tests/MobileSdkTests.generated';
import {
	MobileSdkTests,
	RecentMobileSdkTests
} from 'src/apps/mobile-sdk/services/queries/tests/MobileSdkTests.gql';
import { buildMobileSdkUsageDataVariables } from 'src/apps/mobile-sdk/services/queries/usage-data/helpers/buildVariables';
import { transformMobileSdkUsageDataResults } from 'src/apps/mobile-sdk/services/queries/usage-data/helpers/transformResults';
import { MobileSdkUsageDataQuery } from 'src/apps/mobile-sdk/services/queries/usage-data/MobileSdkUsageData.generated';
import {
	IMobileSdkTestDetails,
	TMobileSdkTestAllIssuesResult,
	TMobileSdkTestFilterOptions,
	TMobileSdkTestsResult
} from 'src/apps/mobile-sdk/types/MobileSdkServiceTypes';
import { IFilterOptions } from 'src/common/helpers/DataFilterHelper';
import { SummaryItem } from 'src/common/interfaces/SummaryItem';
import { gqlClient } from 'src/common/services/api/GraphQLRequestApi';
import { Logger } from 'src/common/services/Logger';
import { MobileIssueDetails } from 'src/common/types/MobileSingleSessionTypes';

import {
	MobileSdkUsageData,
	RecentMobileSdkUsageData
} from './queries/usage-data/MobileSdkUsageData.gql';

export const getMobileSdkTests = async ({
	isRecentTests,
	...restParams
}: IGetMobileSdkTestsParams): Promise<TMobileSdkTestsResult> => {
	try {
		const variables = buildMobileSdkTestsQueryVariables(restParams);

		const query = isRecentTests ? RecentMobileSdkTests : MobileSdkTests;
		const result = await gqlClient.request<MobileSdkTestsQuery>(query, variables);

		return transformMobileSdkTestsResults(result);
	} catch (error) {
		Logger.error('MobileSdkDashboardService -> getMobileSdkTests failed with an error:', error);
		throw error;
	}
};

export const getMobileSdkFilterOptions = async (): Promise<IMobileSdkFilterOptions> => {
	try {
		const result = await gqlClient.request<MobileSdkFilterOptionsQuery>(MobileSdkFilterOptions);

		return transformMobileSdkFilterOptionsResults(result);
	} catch (error) {
		Logger.error('MobileSdkDashboardService -> getMobileSdkFilterOptions GraphQL failed:', error);
		throw error;
	}
};

export const getMobileSdkSummaryData = async (
	filters: IFilterOptions,
	isRecentTests: boolean
): Promise<SummaryItem[]> => {
	try {
		const variables = buildMobileSdkUsageDataVariables(filters);

		const query = isRecentTests ? RecentMobileSdkUsageData : MobileSdkUsageData;
		const result = await gqlClient.request<MobileSdkUsageDataQuery>(query, variables);

		return transformMobileSdkUsageDataResults(result, filters.sessionsPeriod?.[0]);
	} catch (e) {
		Logger.error('MobileSdkDashboardService -> getMobileSdkSummaryData GraphQL query failed:', e);
		throw e;
	}
};

export const getMobileSdkTestDetails = async (
	testId: string
): Promise<IMobileSdkTestDetails | null> => {
	try {
		const variables = buildMobileSdkTestDetailsQueryVariables(testId);
		const result = await gqlClient.request<MobileSdkTestDetailsQuery>(
			MobileSdkTestDetails,
			variables
		);

		return transformMobileSdkTestDetailsResults(result);
	} catch (e) {
		Logger.error('MobileSdkDashboardService -> getMobileSdkTestDetails failed with an error:', e);
		throw e;
	}
};

export const getMobileSdkTestAllIssues = async (
	params: IGetMobileSdkTestIssuesParams
): Promise<TMobileSdkTestAllIssuesResult> => {
	try {
		const variables = buildMobileSdkTestIssuesQueryVariables(params);
		const result = await gqlClient.request<MobileSdkTestIssuesQuery>(
			MobileSdkTestIssues,
			variables
		);

		return transformMobileSdkTestIssuesResults(result);
	} catch (error) {
		Logger.error(
			'MobileSdkDashboardService -> getMobileSdkTestAllIssues failed with an error:',
			error
		);
		throw error;
	}
};

export const getMobileSdkTestFilterOptions = async (
	testId: string
): Promise<TMobileSdkTestFilterOptions> => {
	try {
		const variables = buildMobileSdkTestFilterOptionsVariables(testId);
		const result = await gqlClient.request<MobileSdkTestFilterOptionsQuery>(
			MobileSdkTestFilterOptions,
			variables
		);

		return transformMobileSdkTestFilterOptionsResults(result);
	} catch (e) {
		Logger.error(
			'MobileSdkDashboardService -> getMobileSdkTestFilterOptions failed with an error:',
			e
		);
		throw e;
	}
};

export const getMobileSdkIssueDetails = async (
	issueId: string,
	testId: string
): Promise<MobileIssueDetails> => {
	try {
		const variables = buildMobileSdkIssueDetailsVariables(issueId, testId);
		const result = await gqlClient.request<MobileSdkIssueDetailsQuery>(
			MobileSdkIssueDetails,
			variables
		);

		return transformMobileSdkIssueDetailsResults(result);
	} catch (e) {
		Logger.error('MobileSdkDashboardService -> getMobileSdkIssueDetails failed with an error:', e);
		throw e;
	}
};
